import { useFirmReportCenter, useSetFirmDashboards } from '@hooks/useFirmSettings'

/**
 * CC-50286 — the firm-wide Dashboards switch (spec 083).
 *
 * Sits directly under the PER-USER Dashboards opt-in in Settings → Report Center,
 * because the two are read together and the firm value overrides the personal one.
 *
 * Rendering rules, in priority order:
 *   - `canManage` → the admin gets the switch.
 *   - firm switch OFF → EVERYONE gets told, admin or not. Without this the
 *     personal toggle above silently does nothing, which is the same dead-end
 *     spec 076 §3 fixed for the Queue tab: a control that advertises an outcome
 *     it cannot produce.
 *   - permission unresolvable → say so, rather than hiding the control. An admin
 *     on a degraded environment would otherwise conclude the feature shipped
 *     broken. (`CB_AUTHORIZE_SECRET` is unbound on cb-api-qa, which is exactly how
 *     an admin-only control becomes invisible to everyone.)
 *   - genuinely not an admin, firm switch on → render nothing. Nothing to say.
 */
const FirmDashboardsSetting = () => {
  const { data, isLoading, isError } = useFirmReportCenter()
  const setFirm = useSetFirmDashboards()

  if (isLoading || isError || !data) return null

  const { dashboardsEnabled, canManage, permissionResolved } = data
  const firmOff = dashboardsEnabled === false

  if (!canManage && !firmOff && permissionResolved !== false) return null

  return (
    <div className="mt-5 pt-5 border-t border-gray-100">
      {canManage ? (
        <>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">
                Dashboards for the whole firm
                <span className="text-[10px] font-semibold uppercase tracking-wide align-middle ml-1.5 px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">
                  Admin
                </span>
              </p>
              <p className="text-xs text-gray-500">
                Turn this off if your firm does not use Dashboards — nobody will see the
                Dashboards tab, whatever their personal setting.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={!firmOff}
              aria-label="Enable Dashboards for the whole firm"
              disabled={setFirm.isPending}
              onClick={() => setFirm.mutate(firmOff)}
              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
                firmOff ? 'bg-gray-200' : 'bg-cb-accent'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${
                  firmOff ? 'translate-x-1' : 'translate-x-6'
                }`}
              />
            </button>
          </div>
          {firmOff && (
            <p className="text-xs text-gray-500 mt-2">
              Dashboards are off for the firm, so the personal setting above has no effect
              for anyone.
            </p>
          )}
          {setFirm.isError && (
            <p className="text-xs text-red-600 mt-3">
              {setFirm.error?.response?.data?.error || 'Couldn’t save — try again.'}
            </p>
          )}
        </>
      ) : firmOff ? (
        <p className="text-xs text-gray-500">
          Your firm has turned Dashboards off, so the setting above has no effect. An
          administrator can change this.
        </p>
      ) : (
        <p className="text-xs text-gray-500">
          The firm-wide Dashboards setting couldn’t be checked, so it is not shown. Your
          personal setting above still applies.
        </p>
      )}
    </div>
  )
}

export default FirmDashboardsSetting
